    import React, { useEffect, useState } from "react";
    import { api } from "../../api/client";
    import { useNavigate } from 'react-router-dom';

    // Helper to format role names
    const formatRole = (role) => {
        if (!role) return '—';
        return role
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Plain text status pill, one per line: "<Label> Approved" / "<Label> Pending".
    // Fixed width so all three pills line up the same size regardless of text length.
    const StatusChip = ({ label, approved }) => (
        <span className={`px-2 py-1 rounded text-xs font-medium w-32 text-center ${
            approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
        }`}>
            {label} {approved ? 'Approved' : 'Pending'}
        </span>
    );

    export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [credentialModalUser, setCredentialModalUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
        const res = await api.get("/api/admin/users");
        setUsers(res.data);
        } catch (err) {
        console.error('Failed to fetch users:', err);
        alert('Failed to load users');
        } finally {
        setLoading(false);
        }
    };

    const toggleEvents = async (user) => {
        const approved = user.events_approved;
        if (approved && !window.confirm(`Revoke ${user.email}'s access to writeback events and event types?`)) return;
        try {
            await api.post(approved ? '/api/revoke-user-events' : '/api/approve-user-events', { email: user.email });
            setUsers(prev => prev.map(u => u.email === user.email ? { ...u, events_approved: !approved } : u));
        } catch (err) {
            console.error('Failed to update events access:', err);
            alert(`Failed to ${approved ? 'revoke' : 'approve'} events access`);
        }
    };

    const toggleCredential = async (email, credentialType, currentlyApproved) => {
        const label = credentialType === 'sandbox' ? 'Sandbox (Staging)' : 'Production (Live)';
        const flagKey = credentialType === 'sandbox' ? 'sandbox_credentials_approved' : 'live_credentials_approved';
        if (currentlyApproved && !window.confirm(`Revoke ${label} credentials access for ${email}?`)) return;
        try {
            await api.post(
                currentlyApproved ? '/api/revoke-user-credentials' : '/api/approve-user-credentials',
                { email, credential_type: credentialType }
            );
            // Update in place so the modal and table reflect the new status immediately
            setUsers(prev => prev.map(u => u.email === email ? { ...u, [flagKey]: !currentlyApproved } : u));
            setCredentialModalUser(prev => (prev && prev.email === email) ? { ...prev, [flagKey]: !currentlyApproved } : prev);
        } catch (err) {
            console.error(`Failed to update ${label} credentials access:`, err);
            alert(`Failed to ${currentlyApproved ? 'revoke' : 'grant'} ${label} credentials access`);
        }
    };

    const viewUserDetails = (userId) => {
        navigate(`/admin/users/${userId}`);
    };

    const filteredUsers = users.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.company?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
        );
    }

    return (
        <div className="space-y-6">
        <h1 className="text-3xl font-bold">All Users</h1>

        <div className="bg-white p-4 rounded-lg shadow">
            <input
            type="text"
            placeholder="Search by email, name, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            />
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-100">
                <tr>
                    <th className="p-3 text-center">ID</th>
                    <th className="p-3 text-center">Full Name</th>
                    <th className="p-3 text-center">Email</th>
                    <th className="p-3 text-center">Company</th>
                    <th className="p-3 text-center">Role</th>
                    <th className="p-3 text-center">Created</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Actions</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                    <td className="p-3 text-center">{user.id}</td>
                    <td className="p-3 text-center">{user.full_name || '—'}</td>
                    <td className="p-3 text-center">{user.email}</td>
                    <td className="p-3 text-center">{user.company || '—'}</td>
                    <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                        user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                        {formatRole(user.role)}
                        </span>
                    </td>
                    <td className="p-3 text-sm text-center">
                        {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                        <div className="flex flex-col items-center gap-1">
                        <StatusChip label="Events" approved={user.events_approved} />
                        <StatusChip label="Sandbox" approved={user.sandbox_credentials_approved} />
                        <StatusChip label="Live" approved={user.live_credentials_approved} />
                        </div>
                    </td>
                    <td className="p-3">
                        <div className="flex flex-col items-center gap-2">
                        <button
                        onClick={() => viewUserDetails(user.id)}
                        className="text-blue-600 hover:underline text-sm"
                        >
                        View
                        </button>
                        {user.role !== 'admin' && (
                        <button
                            onClick={() => toggleEvents(user)}
                            className={`hover:underline text-sm ${user.events_approved ? 'text-red-600' : 'text-green-600'}`}
                        >
                            {user.events_approved ? 'Revoke Events' : 'Approve Events'}
                        </button>
                        )}
                        {user.role !== 'admin' && (
                        <button
                            onClick={() => setCredentialModalUser(user)}
                            className="text-emerald-700 hover:underline text-sm"
                        >
                            Manage Credentials
                        </button>
                        )}
                        </div>
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
        </div>

        {credentialModalUser && (
            <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setCredentialModalUser(null)}
            >
            <div
                className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-lg font-semibold">Manage Credentials</h2>
                <p className="text-sm text-gray-600 mb-4">{credentialModalUser.email}</p>
                <p className="text-sm text-gray-700 mb-4">
                Grant or revoke access per credential type:
                </p>

                <div className="space-y-3">
                <button
                    onClick={() => toggleCredential(credentialModalUser.email, 'sandbox', credentialModalUser.sandbox_credentials_approved)}
                    className="w-full flex items-center justify-between px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                >
                    <span>
                    <span className="font-medium block">Sandbox (Staging)</span>
                    <span className="text-xs text-gray-500">For testing in the sandbox environment</span>
                    </span>
                    <span className={`px-2 py-1 rounded text-xs shrink-0 ${
                    credentialModalUser.sandbox_credentials_approved
                        ? 'bg-red-100 text-red-700'
                        : 'bg-blue-600 text-white'
                    }`}>
                    {credentialModalUser.sandbox_credentials_approved ? 'Revoke' : 'Grant'}
                    </span>
                </button>

                <button
                    onClick={() => toggleCredential(credentialModalUser.email, 'live', credentialModalUser.live_credentials_approved)}
                    className="w-full flex items-center justify-between px-4 py-3 border rounded-lg hover:bg-gray-50 text-left"
                >
                    <span>
                    <span className="font-medium block">Production (Live)</span>
                    <span className="text-xs text-gray-500">For real production access</span>
                    </span>
                    <span className={`px-2 py-1 rounded text-xs shrink-0 ${
                    credentialModalUser.live_credentials_approved
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-600 text-white'
                    }`}>
                    {credentialModalUser.live_credentials_approved ? 'Revoke' : 'Grant'}
                    </span>
                </button>
                </div>

                <div className="mt-5 text-right">
                <button
                    onClick={() => setCredentialModalUser(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:underline"
                >
                    Close
                </button>
                </div>
            </div>
            </div>
        )}
        </div>
    );
    }
